const NETWORK_CODES = new Set(['REQUEST_TIMEOUT', 'RIOT_CLIENT_TIMEOUT', 'ETIMEDOUT',
    'ECONNABORTED', 'ECONNRESET', 'ENETUNREACH', 'EAI_AGAIN', 'ERR_NETWORK']);
const EXPIRED_CODES = new Set(['AUTH_FAILURE', 'AUTHENTICATION_FAILED', 'UNAUTHORIZED',
    'INVALID_TOKEN', 'EXPIRED_TOKEN', 'TOKEN_EXPIRED', 'INVALID_ACCESS_TOKEN', 'ACCESS_TOKEN_EXPIRED',
    'INVALID_SESSION', 'SESSION_EXPIRED', 'RIOT_SESSION_EXPIRED']);

function failure(code, message = code) { return Object.assign(new Error(message), { code }); }
function isTransientNetworkError(error) {
    if (!error) return false;
    if (Number(error.httpStatus) || Number(error.response && error.response.status)
        || Number(error.diagnostic && error.diagnostic.httpStatus) || error.businessFailure) return false;
    return error.transientNetwork === true || NETWORK_CODES.has(error.code)
        || (!error.code && error.request && !error.response && error.message === 'Network Error');
}

async function readWithRetry(read, options = {}) {
    const check = options.check || (() => {});
    const now = options.now || Date.now;
    const deadline = options.deadline === undefined ? Infinity : options.deadline;
    const budget = options.budget || { remaining: 1 };
    const delay = options.retryDelayMs === undefined ? 400 : Math.max(0, options.retryDelayMs);
    for (let attempt = 0; attempt < 2; attempt++) {
        check();
        try {
            const result = await read(attempt);
            check();
            return result;
        } catch (error) {
            check();
            if (attempt || budget.remaining <= 0 || !isTransientNetworkError(error)
                || deadline - now() < delay + 1000) throw error;
            budget.remaining--;
            if (delay) await new Promise(resolve => setTimeout(resolve, delay));
            check();
            if (deadline - now() < 1000) throw error;
        }
    }
}

function decodeJson(value) {
    if (typeof value !== 'string') return value;
    try { return JSON.parse(value); }
    catch (_) {
        try { return JSON.parse(decodeURIComponent(value)); }
        catch (_) { return null; }
    }
}

function validateResponse(url, headers, data, status) {
    const credentialed = /^https:\/\/(?:auth\.riotgames\.com|entitlements\.auth\.riotgames\.com|pd\.(?:ap|na|eu|kr|br|latam)\.a\.pvp\.net)(?:\/|$)/i.test(url)
        && Object.keys(headers).some(key => key.toLowerCase() === 'authorization' && /^Bearer\s+\S+/i.test(headers[key]));
    const businessCode = String(data && (data.errorCode || data.error) || '').toUpperCase();
    const fail = code => { throw Object.assign(failure(code), { httpStatus: status, businessFailure: Boolean(businessCode) }); };
    if (status === 401 && credentialed) fail('RIOT_SESSION_EXPIRED');
    if (status === 429) fail('RIOT_RATE_LIMITED');
    if (status === 404) fail('RIOT_NOT_FOUND');
    if ((!status || status === 200 || status === 403) && credentialed && EXPIRED_CODES.has(businessCode)) fail('RIOT_SESSION_EXPIRED');
    if (status && status !== 200) fail('RIOT_UPSTREAM_UNAVAILABLE');
    if (/RATE|TOO_MANY/.test(businessCode)) fail('RIOT_RATE_LIMITED');
    if (businessCode) fail('RIOT_RESPONSE_INVALID');
}

function parseSdkResponse(url, headers, response, responseHeaders) {
    const data = decodeJson(response);
    const metadata = decodeJson(responseHeaders);
    const status = Number(metadata && metadata.httpStatus || data && data.httpStatus) || 0;
    validateResponse(url, headers, data, status);
    if (data === null || typeof data !== 'object') throw failure('RIOT_RESPONSE_INVALID');
    return { data, status };
}

// For non-archived identity/version/token responses only. Never manufacture raw archive text.
function sdkJsonRequest(appApi, url, method, headers, body, options = {}) {
    const startedAt = Date.now();
    const diagnostic = { callbackReceived: false, responseType: 'unreceived', httpStatus: 0, bodyChars: 0 };
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (error, result) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if (options.isCurrent && !options.isCurrent()) error = failure(options.cancelCode || 'RIOT_CLIENT_CANCELLED');
            if (error) reject(Object.assign(error, { diagnostic: { ...diagnostic, elapsedMs: Date.now() - startedAt } }));
            else resolve(result);
        };
        const timer = setTimeout(() => finish(failure(options.timeoutCode || 'RIOT_CLIENT_TIMEOUT',
            options.timeoutMessage || options.timeoutCode || 'RIOT_CLIENT_TIMEOUT')), options.timeout || 15000);
        try {
            if (options.isCurrent && !options.isCurrent()) return finish(failure(options.cancelCode || 'RIOT_CLIENT_CANCELLED'));
            if (!appApi || !appApi.Net || typeof appApi.Net.send !== 'function') return finish(failure('RIOT_CLIENT_RAW_UNAVAILABLE'));
            const dispatched = appApi.Net.send(url, method, 'application/json', headers, body, (response, responseHeaders) => {
                if (settled) return;
                diagnostic.callbackReceived = true;
                diagnostic.responseType = response === null ? 'null' : Array.isArray(response) ? 'array' : typeof response;
                diagnostic.bodyChars = typeof response === 'string' ? response.length : 0;
                try {
                    const data = decodeJson(response);
                    const metadata = decodeJson(responseHeaders);
                    const status = Number(metadata && metadata.httpStatus || data && data.httpStatus) || 0;
                    diagnostic.httpStatus = status;
                    validateResponse(url, headers, data, status);
                    if (data === null || typeof data !== 'object') throw failure('RIOT_RESPONSE_INVALID');
                    finish(null, { data, status });
                } catch (error) { finish(error); }
            });
            if (dispatched === false) finish(failure('RIOT_CLIENT_RAW_UNAVAILABLE'));
        } catch (error) {
            finish(Object.assign(failure('RIOT_UPSTREAM_UNAVAILABLE'), { transientNetwork: isTransientNetworkError(error) }));
        }
    });
}

module.exports = { isTransientNetworkError, readWithRetry, sdkJsonRequest, parseSdkResponse, decodeJson };
